import { setQueriesData } from '@leagueplatform/core';
import { type MasonryEngineStateControllerStore } from './masonry-engine-state-controller';
import {
  type MasonryNodeMessageReceiver,
  type MasonryNodeMessages,
  type MasonryInstanceMessages,
  type MessageReturn,
} from './types/masonry-engine-message.types';
import { type MasonryEngineNode } from './types/masonry-engine-node.types';
import { type NarrowByType } from './types/util.types';
import { QUERY_KEY_GET_NODE_ASYNC_SELF } from './constants/constants';

/**
 * An object that allows nodes to register their `MasonryNodeMessageReceiver`,
 * and allows actions to send messages to those receivers.
 *
 * It also allows sending "instance messages", which perform various
 * instance-level operations.
 */
export type MasonryEngineMessagingController = {
  registerReceiver: (
    nodeId: MasonryEngineNode['id'],
    receiver: MasonryNodeMessageReceiver<MasonryNodeMessages>,
  ) => void;
  unregisterReceiver: (nodeId: MasonryEngineNode['id']) => void;
  sendMessage: <Type extends MasonryNodeMessages['type']>(
    nodeId: MasonryEngineNode['id'],
    message: NarrowByType<MasonryNodeMessages, Type>,
  ) => MessageReturn<NarrowByType<MasonryNodeMessages, Type>>;
  sendMessageToInstance: <Type extends MasonryInstanceMessages['type']>(
    message: NarrowByType<MasonryInstanceMessages, Type>,
  ) => MessageReturn<NarrowByType<MasonryInstanceMessages, Type>>;
};

export const createMessagingController = (
  stateControllerStore: MasonryEngineStateControllerStore,
): MasonryEngineMessagingController => {
  const nodeMessageReceiverMap = new Map<
    MasonryEngineNode['id'],
    MasonryNodeMessageReceiver<MasonryNodeMessages>
  >();

  const instanceReceiver: MasonryNodeMessageReceiver<MasonryInstanceMessages> =
    {
      seedAsyncNodeCache: ({ payload: { nodeData } }) => {
        stateControllerStore
          .getState()
          .instance.asyncNodeCacheSeeds.set(nodeData.id, nodeData);
      },
      replaceAsyncNodeCache: ({ payload: { nodeData } }) => {
        setQueriesData([QUERY_KEY_GET_NODE_ASYNC_SELF, nodeData.id], nodeData);
      },
      showModal: (message) => {
        stateControllerStore.setState((current) => ({
          ...current,
          instance: {
            ...current.instance,
            modalState: {
              ...message.payload,
              /**
               * We will only update the elementToFocusOnClose if there isn't
               * already a modal open. This ensures that if multiple `showModal`
               * messages are received before any `dismissModal` (e.g., to update
               * the contents of an already-open modal), we always return focus to the element
               * that actually opened the modal in the first place.
               */
              elementToFocusOnClose:
                current.instance.modalState?.elementToFocusOnClose ??
                message.payload.elementToFocusOnClose,
            },
          },
        }));
      },
      dismissModal: () => {
        const {
          instance: { modalState },
        } = stateControllerStore.getState();
        if (!modalState) {
          return;
        }

        stateControllerStore.setState((current) => ({
          ...current,
          instance: {
            ...current.instance,
            modalState: undefined,
          },
        }));
      },
    };

  return {
    registerReceiver: (nodeId, receiver) => {
      nodeMessageReceiverMap.set(nodeId, receiver);
    },
    unregisterReceiver: (nodeId) => {
      nodeMessageReceiverMap.delete(nodeId);
    },
    sendMessage: (nodeId, message) => {
      const receiver = nodeMessageReceiverMap.get(nodeId);

      if (!receiver) {
        throw new Error(
          `MasonryEngine - Could not find message receiver for node ${nodeId}`,
        );
      }
      const handler = receiver[message.type];
      return handler(message);
    },
    sendMessageToInstance: (message) => instanceReceiver[message.type](message),
  };
};
